import { useState, useEffect } from "react";
import { ProgramacaoCalendar } from "../components/ProgramacaoCalendar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatDateBR, formatDateShort, parseDate } from "../utils/dateUtils";
import {
  ExternalLink,
  Link as LinkIcon,
  Calendar,
  Instagram,
  Youtube,
  Facebook,
  MessageCircle,
  Globe,
  Music,
  Heart,
  BookOpen,
  Phone,
  LogIn,
  UserPlus,
  MapPin,
  Clock,
  Users,
  ArrowRight,
  ChevronRight,
  Download,
  Copy,
  Check,
  Sparkles,
  Play,
  Newspaper,
  Plus,
  ChevronDown,
  Wifi,
  Loader2,
  CheckCircle2,
  User,
  Mail,
  ClipboardList,
  GraduationCap,
  Dumbbell,
  Brain,
  HandHeart
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconOptions = {
  link: LinkIcon,
  instagram: Instagram,
  youtube: Youtube,
  facebook: Facebook,
  whatsapp: MessageCircle,
  website: Globe,
  spotify: Music,
  donate: Heart,
  bible: BookOpen,
  contact: Phone
};

const getIconComponent = (iconName) => {
  return iconOptions[iconName] || LinkIcon;
};

export default function NewsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [newsConfig, setNewsConfig] = useState(null);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [agendaItems, setAgendaItems] = useState([]);
  const [programacaoBlocos, setProgramacaoBlocos] = useState([]);
  const [inscricaoBlocos, setInscricaoBlocos] = useState([]);
  const [systemSettings, setSystemSettings] = useState(null);
  const [branchInfo, setBranchInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [installStatus, setInstallStatus] = useState('available');
  const [pixCopied, setPixCopied] = useState(false);
  const [carrosselIndex, setCarrosselIndex] = useState(0); // 0 = banner, 1 = video
  
  // Detect user's device/browser for install instructions
  const getDeviceInfo = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /android/i.test(ua);
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    const isChrome = /chrome/i.test(ua) && !/edge/i.test(ua);
    const isSamsung = /samsungbrowser/i.test(ua);
    const isFirefox = /firefox/i.test(ua);
    const isEdge = /edg/i.test(ua);
    
    return { isIOS, isAndroid, isSafari, isChrome, isSamsung, isFirefox, isEdge };
  };
  
  const deviceInfo = getDeviceInfo();

  // WiFi Portal states
  const [isWifiPortal, setIsWifiPortal] = useState(false);
  const [showWifiModal, setShowWifiModal] = useState(false);
  const [wifiSuccess, setWifiSuccess] = useState(null); // null, 'success', 'auto'
  const [wifiSubmitting, setWifiSubmitting] = useState(false);
  const [wifiFormConfig, setWifiFormConfig] = useState(null);
  const [wifiTab, setWifiTab] = useState("register"); // "login" or "register"
  const [wifiParams, setWifiParams] = useState({});
  const [ministries, setMinistries] = useState([]);

  // WiFi Login form
  const [wifiLoginForm, setWifiLoginForm] = useState({ telefone: "", email: "" });
  
  // WiFi Register form
  const [wifiRegisterForm, setWifiRegisterForm] = useState({
    nome_completo: "",
    telefone: "",
    email: "",
    senha: "",
    confirmar_senha: "",
    data_nascimento: "",
    sexo: "",
    cidade: "",
    endereco: "",
    estado_civil: "",
    situacao: "",
    batismo: "",
    ministerios_desejados: []
  });

  const primaryColor = systemSettings?.cor_primaria || "#000000";
  const secondaryColor = systemSettings?.cor_secundaria || "#e11d48";

  // Copy Pix key to clipboard
  const handleCopyPix = async () => {
    const pixKey = newsConfig?.chave_pix;
    if (!pixKey) return;

    try {
      await navigator.clipboard.writeText(pixKey);
      setPixCopied(true);
      toast.success("Chave Pix copiada!");
      setTimeout(() => setPixCopied(false), 3000);
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = pixKey;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setPixCopied(true);
      toast.success("Chave Pix copiada!");
      setTimeout(() => setPixCopied(false), 3000);
    }
  };

  // Check if app is in standalone mode (already installed)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  // Detect WiFi Portal mode from URL params
  useEffect(() => {
    const clientMac = searchParams.get("clientMac") || searchParams.get("client_mac") || "";
    const clientIp = searchParams.get("clientIP") || searchParams.get("client_ip") || "";
    const apMac = searchParams.get("apMac") || searchParams.get("ap_mac") || "";
    const ssidName = searchParams.get("ssidName") || searchParams.get("ssid") || "";
    const originalUrl = searchParams.get("originalUrl") || searchParams.get("redirect") || "";
    const filialId = searchParams.get("filial_id") || searchParams.get("filial") || "";
    const wifiMode = searchParams.get("wifi") || "";

    // Check if this is a WiFi portal access
    if (clientMac || wifiMode === "true") {
      setIsWifiPortal(true);
      setWifiParams({ clientMac, clientIp, apMac, ssidName, originalUrl, filialId });
      
      // Check if MAC is known for auto-login
      if (clientMac) {
        checkMacAddress(clientMac, filialId);
      }
    }
  }, [searchParams]);

  // Check if MAC address is known
  const checkMacAddress = async (mac, filialId) => {
    try {
      const response = await axios.post(`${API}/wifi/portal/check-mac`, {
        client_mac: mac,
        filial_id: filialId
      });
      
      if (response.data.known) {
        // Auto-login for known device
        handleWifiAutoLogin(mac, filialId);
      } else {
        // Show WiFi modal for registration
        setShowWifiModal(true);
      }
    } catch (error) {
      // If check fails, show modal anyway
      setShowWifiModal(true);
    }
  };

  // Auto-login for known MAC address
  const handleWifiAutoLogin = async (mac, filialId) => {
    setWifiSubmitting(true);
    try {
      const response = await axios.post(`${API}/wifi/portal/auto-login`, {
        client_mac: mac,
        client_ip: wifiParams.clientIp,
        ap_mac: wifiParams.apMac,
        ssid_name: wifiParams.ssidName,
        original_url: wifiParams.originalUrl,
        filial_id: filialId
      });

      if (response.data.success) {
        setWifiSuccess({
          type: "auto",
          pessoa: response.data.pessoa,
          igreja: response.data.igreja
        });
        toast.success(response.data.message);
      }
    } catch (error) {
      // If auto-login fails, show modal for manual login
      setShowWifiModal(true);
    } finally {
      setWifiSubmitting(false);
    }
  };

  // Load WiFi form config
  const loadWifiConfig = async (filialId) => {
    try {
      const [configRes, ministriesRes] = await Promise.all([
        axios.get(`${API}/wifi/portal/config/${filialId || "default"}`).catch(() => ({ data: null })),
        axios.get(`${API}/ministries/public${filialId ? `?filial_id=${filialId}` : ""}`).catch(() => ({ data: [] }))
      ]);
      setWifiFormConfig(configRes.data);
      setMinistries(ministriesRes.data || []);
    } catch (error) {
      console.log("Error loading WiFi config");
    }
  };

  // Load WiFi config when modal opens
  useEffect(() => {
    if (showWifiModal) {
      loadWifiConfig(wifiParams.filialId || branchInfo?.id);
    }
  }, [showWifiModal, wifiParams.filialId, branchInfo]);

  // PWA Install prompt handler
  useEffect(() => {
    if (isStandalone) {
      setInstallStatus('installed');
      return;
    }

    // Check if there's a globally stored prompt (captured before component mounted)
    if (window.deferredPWAPrompt) {
      setDeferredPrompt(window.deferredPWAPrompt);
      setInstallStatus('available');
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      // Store globally for persistence
      window.deferredPWAPrompt = e;
      setDeferredPrompt(e);
      setInstallStatus('available');
      console.log('[PWA] Install prompt captured and ready');
    };

    const handleAppInstalled = () => {
      window.deferredPWAPrompt = null;
      setDeferredPrompt(null);
      setInstallStatus('installed');
      toast.success("App instalado com sucesso!");
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isStandalone]);

  const handleInstallClick = async () => {
    // Method 1: Use deferred prompt if available (Chrome, Edge, Samsung)
    if (deferredPrompt || window.deferredPWAPrompt) {
      const prompt = deferredPrompt || window.deferredPWAPrompt;
      try {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        if (outcome === 'accepted') {
          window.deferredPWAPrompt = null;
          setDeferredPrompt(null);
          setInstallStatus('installed');
          toast.success("App instalado com sucesso!");
        }
        return;
      } catch (err) {
        console.log("Install prompt error:", err);
      }
    }
    
    // Method 2: Try to trigger install via navigator if supported
    if ('getInstalledRelatedApps' in navigator) {
      try {
        const relatedApps = await navigator.getInstalledRelatedApps();
        if (relatedApps.length > 0) {
          toast.info("O app já está instalado!");
          setInstallStatus('installed');
          return;
        }
      } catch (err) {
        console.log("Related apps check error:", err);
      }
    }
    
    // Method 3: For iOS Safari, show specific instructions
    if (deviceInfo.isIOS) {
      setShowInstallModal(true);
      return;
    }
    
    // Method 4: For Android without install prompt, show modal with instructions
    if (deviceInfo.isAndroid) {
      setShowInstallModal(true);
      return;
    }
    
    // Fallback: Show modal with instructions for all platforms
    setShowInstallModal(true);
  };

  // WiFi Login handler
  const handleWifiLogin = async (e) => {
    e.preventDefault();
    if (!wifiLoginForm.telefone && !wifiLoginForm.email) {
      toast.error("Informe seu telefone ou email");
      return;
    }

    setWifiSubmitting(true);
    try {
      const response = await axios.post(`${API}/wifi/portal/login`, {
        telefone: wifiLoginForm.telefone,
        email: wifiLoginForm.email || null,
        client_mac: wifiParams.clientMac,
        client_ip: wifiParams.clientIp,
        ap_mac: wifiParams.apMac,
        ssid_name: wifiParams.ssidName,
        original_url: wifiParams.originalUrl,
        filial_id: wifiParams.filialId || branchInfo?.id
      });

      if (response.data.success) {
        setWifiSuccess({
          type: "login",
          pessoa: response.data.pessoa,
          igreja: response.data.igreja
        });
        setShowWifiModal(false);
        toast.success(response.data.message);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao fazer login";
      toast.error(message);
      
      // If not found, suggest registration
      if (error.response?.status === 404) {
        setWifiTab("register");
        if (wifiLoginForm.telefone) {
          setWifiRegisterForm(prev => ({ ...prev, telefone: wifiLoginForm.telefone }));
        }
        if (wifiLoginForm.email) {
          setWifiRegisterForm(prev => ({ ...prev, email: wifiLoginForm.email }));
        }
      }
    } finally {
      setWifiSubmitting(false);
    }
  };

  // WiFi Register handler
  const handleWifiRegister = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!wifiRegisterForm.nome_completo || !wifiRegisterForm.telefone) {
      toast.error("Nome e telefone são obrigatórios");
      return;
    }

    // Validate password if email is provided (user will be created)
    if (wifiRegisterForm.email) {
      if (!wifiRegisterForm.senha) {
        toast.error("Informe uma senha para acessar o sistema");
        return;
      }
      if (wifiRegisterForm.senha.length < 6) {
        toast.error("A senha deve ter pelo menos 6 caracteres");
        return;
      }
      if (wifiRegisterForm.senha !== wifiRegisterForm.confirmar_senha) {
        toast.error("As senhas não coincidem");
        return;
      }
    }

    setWifiSubmitting(true);
    try {
      // Remove confirmar_senha before sending to backend
      const { confirmar_senha, ...formData } = wifiRegisterForm;
      
      const response = await axios.post(`${API}/wifi/portal/register`, {
        ...formData,
        client_mac: wifiParams.clientMac,
        client_ip: wifiParams.clientIp,
        ap_mac: wifiParams.apMac,
        ssid_name: wifiParams.ssidName,
        original_url: wifiParams.originalUrl,
        filial_id: wifiParams.filialId || branchInfo?.id
      });

      if (response.data.success) {
        setWifiSuccess({
          type: "register",
          pessoa: response.data.pessoa,
          igreja: response.data.igreja
        });
        setShowWifiModal(false);
        toast.success(response.data.message);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao fazer cadastro";
      toast.error(message);
    } finally {
      setWifiSubmitting(false);
    }
  };

  // Close WiFi success screen
  const handleCloseWifiSuccess = () => {
    setWifiSuccess(null);
    setIsWifiPortal(false);
    // Clear URL params
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const getLogoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, configRes, newsRes, eventsRes, agendaRes, blocosRes, branchRes, inscricaoBlocosRes] = await Promise.all([
        axios.get(`${API}/settings/system/public`).catch(() => ({ data: null })),
        axios.get(`${API}/news-config/public`).catch(() => ({ data: null })),
        axios.get(`${API}/news/public`).catch(() => ({ data: [] })),
        axios.get(`${API}/events/public`).catch(() => ({ data: [] })),
        axios.get(`${API}/agenda/public`).catch(() => ({ data: [] })),
        axios.get(`${API}/agenda/blocos/public`).catch(() => ({ data: [] })),
        axios.get(`${API}/branch/public`).catch(() => ({ data: null })),
        axios.get(`${API}/blocos-inscricao/public`).catch(() => ({ data: [] }))
      ]);
      
      setSystemSettings(settingsRes.data);
      setNewsConfig(configRes.data);
      setNews(newsRes.data || []);
      setEvents(eventsRes.data || []);
      setAgendaItems(agendaRes.data || []);
      setProgramacaoBlocos(blocosRes.data || []);
      setBranchInfo(branchRes.data);
      setInscricaoBlocos(inscricaoBlocosRes.data || []);
    } catch (error) {
      console.log("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatEventDay = (dateStr) => {
    try {
      const date = parseDate(dateStr);
      if (!date) return "";
      return format(date, "dd");
    } catch {
      return "";
    }
  };

  const formatEventMonth = (dateStr) => {
    try {
      const date = parseDate(dateStr);
      if (!date) return "";
      return format(date, "MMM", { locale: ptBR }).toUpperCase();
    } catch {
      return "";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div 
            className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin"
            style={{ borderColor: `${primaryColor}20`, borderTopColor: primaryColor }}
          />
        </div>
      </div>
    );
  }

  const banner = newsConfig?.banner || {};
  const links = (newsConfig?.links || []).filter(l => l.ativo !== false).sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
  const showEvents = newsConfig?.mostrar_eventos !== false;
  const showNews = newsConfig?.mostrar_noticias !== false;
  const eventsLimit = newsConfig?.eventos_limite || 3;
  const newsLimit = newsConfig?.noticias_limite || 6;
  const upcomingEvents = events.slice(0, eventsLimit);
  const displayNews = news.slice(0, newsLimit);

  // WiFi Success Screen
  if (wifiSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-8 text-center">
            <div 
              className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <CheckCircle2 className="h-14 w-14" style={{ color: primaryColor }} />
            </div>
            
            <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-gray-100 mb-3">
              {wifiSuccess.type === "auto" ? "Bem-vindo de volta!" : "Conectado!"}
            </h2>
            
            <p className="text-xl font-semibold mb-2" style={{ color: primaryColor }}>
              Seja bem-vindo à {wifiSuccess.igreja?.nome || branchInfo?.nome || "Igreja"}
            </p>
            
            <p className="text-gray-600 mb-4">
              Olá, <strong>{wifiSuccess.pessoa?.nome || "Visitante"}</strong>!
            </p>
            
            {wifiSuccess.igreja?.visao && (
              <p className="text-sm text-gray-500 italic mb-6 px-4">
                "{wifiSuccess.igreja.visao}"
              </p>
            )}

            <div className="space-y-3 mt-6">
              <div className="flex items-center justify-center gap-2 text-green-600 text-sm font-medium">
                <Wifi className="h-4 w-4" />
                Internet liberada!
              </div>
              
              {wifiSuccess.type === "register" && (
                <p className="text-xs text-gray-500">
                  Você receberá uma mensagem no WhatsApp com seu usuário e senha.
                </p>
              )}
              
              <Button 
                onClick={handleCloseWifiSuccess}
                className="w-full mt-4 rounded-xl"
                style={{ backgroundColor: primaryColor }}
              >
                Continuar
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      {/* Instagram-style Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-screen-md mx-auto px-4 h-14 flex items-center justify-between">
          {/* Logo Only - No Name */}
          <div className="flex items-center">
            {systemSettings?.logo_url ? (
              <img src={getLogoUrl(systemSettings.logo_url)} alt="Logo" className="h-9 object-contain" />
            ) : (
              <div 
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-lg font-bold"
                style={{ backgroundColor: primaryColor }}
              >
                {(systemSettings?.nome_igreja || "8L").charAt(0)}
              </div>
            )}
          </div>
          
          {/* Header Actions */}
          <div className="flex items-center gap-2">
            {/* Botão "Quero fazer parte" */}
            {banner.botao_texto && banner.botao_link && (
              <Button 
                size="sm"
                className="rounded-full h-9 px-4 font-semibold text-sm bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:shadow-lg hover:scale-105 transition-all"
                onClick={() => {
                  if (banner.botao_link.startsWith('http')) {
                    window.open(banner.botao_link, '_blank');
                  } else {
                    navigate(banner.botao_link);
                  }
                }}
                data-testid="cadastro-header-btn"
              >
                <UserPlus className="h-4 w-4 mr-1.5" />
                {banner.botao_texto}
              </Button>
            )}

            {installStatus !== 'installed' && (
              <Button 
                onClick={handleInstallClick} 
                variant="ghost"
                size="sm"
                className="text-gray-700 hover:bg-gray-100 rounded-full h-9 w-9 p-0 relative"
                data-testid="install-app-btn"
                title={deferredPrompt ? "Instalar App (1 clique)" : "Instalar App"}
              >
                <Download className="h-4 w-4" />
                {/* Green dot indicator when direct install is available */}
                {deferredPrompt && (
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                )}
              </Button>
            )}
            {user ? (
              <Button 
                onClick={() => navigate("/dashboard")} 
                size="sm"
                variant="outline"
                className="rounded-full h-9 px-4 font-semibold text-sm"
                data-testid="go-to-system-btn"
              >
                Acessar
              </Button>
            ) : (
              <Button 
                onClick={() => navigate("/login")} 
                size="sm"
                variant="outline"
                className="rounded-full h-9 px-4 font-semibold text-sm"
                data-testid="login-btn"
              >
                Entrar
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* WiFi Portal Banner */}
      {isWifiPortal && !wifiSuccess && (
        <div 
          className="sticky top-14 z-40 px-4 py-3"
          style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
        >
          <div className="max-w-screen-md mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3 text-white">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Wifi className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-sm">Conecte-se ao WiFi</p>
                <p className="text-xs opacity-80">Cadastre-se para navegar gratuitamente</p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setShowWifiModal(true)}
              className="bg-white hover:bg-gray-100 rounded-full font-semibold"
              style={{ color: primaryColor }}
              data-testid="wifi-connect-btn"
            >
              {wifiSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Conectar"
              )}
            </Button>
          </div>
        </div>
      )}

      <main className="max-w-screen-md mx-auto">
        {/* Profile Section - Instagram Style */}
        <section className="px-4 py-6">
          <div className="flex items-start gap-6 md:gap-10">
            {/* Profile Image */}
            <div className="flex-shrink-0">
              <div 
                className="w-20 h-20 md:w-32 md:h-32 rounded-full p-0.5"
                style={{ background: `linear-gradient(45deg, ${secondaryColor}, ${primaryColor}, ${secondaryColor})` }}
              >
                <div className="w-full h-full rounded-full bg-white p-0.5">
                  {systemSettings?.logo_url ? (
                    <img 
                      src={getLogoUrl(systemSettings.logo_url)} 
                      alt="Logo" 
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <div 
                      className="w-full h-full rounded-full flex items-center justify-center text-white text-2xl md:text-4xl font-bold"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {(branchInfo?.nome || systemSettings?.nome_igreja || "8L").charAt(0)}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Profile Info */}
            <div className="flex-1 pt-1">
              <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-1">
                {branchInfo?.nome || systemSettings?.nome_igreja || "Igreja"}
              </h1>
              
              {/* Sobre Nós - da unidade ou subtítulo do banner */}
              {(branchInfo?.sobre_nos || banner.subtitulo) && (
                <p className="text-gray-600 text-sm md:text-base line-clamp-2">
                  {branchInfo?.sobre_nos || banner.subtitulo}
                </p>
              )}
              
              {/* Botão Contribuir (Pix) - Full width com texto e cor configuráveis */}
              {newsConfig?.mostrar_pix && newsConfig?.chave_pix && (
                <button
                  onClick={handleCopyPix}
                  className={`mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all text-white hover:shadow-lg hover:scale-[1.02]`}
                  style={{ 
                    backgroundColor: pixCopied 
                      ? '#22c55e' 
                      : (newsConfig?.cor_botao_pix || primaryColor)
                  }}
                  data-testid="pix-copy-btn"
                >
                  {/* Pix Icon */}
                  <svg viewBox="0 0 512 512" className="h-4 w-4 fill-current">
                    <path d="M242.4 292.5C247.8 287.1 257.1 287.1 262.5 292.5L339.5 369.5C353.7 383.7 373.9 383.7 388.1 369.5L448 310.6C465.7 292.9 465.7 264.3 448 246.6L371.5 170.1C357.3 155.9 357.3 135.7 371.5 121.5L448 44.97C465.7 27.27 494.3 27.27 512 44.97L512 248.1C512 265.8 483.4 283.5 465.7 283.5L448 283.5L448 310.6L465.7 310.6C483.4 310.6 512 328.3 512 346L512 467C494.3 484.7 465.7 484.7 448 467L371.5 390.5C357.3 376.3 337.1 376.3 322.9 390.5L246.4 467C228.7 484.7 200.1 484.7 182.4 467L64 348.6C46.33 330.9 46.33 302.3 64 284.6L140.4 208.2C154.6 194 154.6 173.8 140.4 159.6L64 83.13C46.33 65.43 46.33 36.87 64 19.17C81.67 1.47 110.3 1.47 128 19.17L204.5 95.67C218.7 109.9 238.9 109.9 253.1 95.67L329.5 19.17C347.2 1.47 375.8 1.47 393.5 19.17"/>
                  </svg>
                  {pixCopied ? (
                    <>
                      <Check className="h-4 w-4" />
                      Chave Copiada!
                    </>
                  ) : (
                    <>
                      {newsConfig?.pix_descricao || 'Contribuir'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Instagram Highlights Style Links */}
        {links.length > 0 && (
          <section className="px-4 pb-4">
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {links.map((link) => {
                const IconComp = getIconComponent(link.icone);
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-1.5 flex-shrink-0 group"
                    data-testid={`social-link-${link.id}`}
                  >
                    <div 
                      className="w-16 h-16 md:w-[72px] md:h-[72px] rounded-full p-0.5 transition-transform group-hover:scale-105"
                      style={{ background: `linear-gradient(45deg, ${link.cor || '#f59e0b'}, ${link.cor || '#ec4899'}, ${link.cor || '#8b5cf6'})` }}
                    >
                      <div className="w-full h-full rounded-full bg-white p-0.5 flex items-center justify-center overflow-hidden">
                        {link.icone_url ? (
                          <img 
                            src={link.icone_url} 
                            alt={link.titulo}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <div 
                            className="w-full h-full rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${link.cor || primaryColor}15` }}
                          >
                            <IconComp className="h-6 w-6 md:h-7 md:w-7" style={{ color: link.cor || primaryColor }} />
                          </div>
                        )}
                      </div>
                    </div>
                    <span className="text-xs font-medium text-gray-700 text-center max-w-[72px] truncate">
                      {link.titulo}
                    </span>
                  </a>
                );
              })}
              {/* Add more highlight placeholder */}
              <div className="flex flex-col items-center gap-1.5 flex-shrink-0 opacity-50">
                <div className="w-16 h-16 md:w-[72px] md:h-[72px] rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center">
                  <Plus className="h-6 w-6 text-gray-400" />
                </div>
                <span className="text-xs font-medium text-gray-500">Novo</span>
              </div>
            </div>
          </section>
        )}

        {/* Content Divider */}
        <div className="border-t border-gray-200" />

        {/* Hero Banner with Video Carousel */}
        {(banner.imagem_url || (banner.video_ativo && (banner.video_url || banner.video_arquivo_url))) && (
          <BannerCarousel 
            banner={banner} 
            carrosselIndex={carrosselIndex}
            setCarrosselIndex={setCarrosselIndex}
          />
        )}

        {/* Events Section - Card Style */}
        {showEvents && upcomingEvents.length > 0 && (
          <section className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">Próximos Eventos</h2>
              <button className="text-sm font-semibold" style={{ color: primaryColor }}>
                Ver todos
              </button>
            </div>
            <div className="space-y-3">
              {upcomingEvents.map((event, index) => {
                // Formatar data completa (ex: "Sábado, 15 de Dezembro")
                const formatFullDate = (dateStr) => {
                  try {
                    const date = parseDate(dateStr);
                    if (!date) return "";
                    return format(date, "EEEE, dd 'de' MMMM", { locale: ptBR });
                  } catch {
                    return "";
                  }
                };
                
                return (
                  <div 
                    key={event.id}
                    className="flex gap-4 p-4 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                    data-testid={`event-card-${event.id}`}
                  >
                    {/* Date */}
                    <div 
                      className="flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center"
                      style={{ backgroundColor: index === 0 ? primaryColor : `${primaryColor}15` }}
                    >
                      <span className={`text-xl font-bold ${index === 0 ? 'text-white' : ''}`} style={{ color: index === 0 ? 'white' : primaryColor }}>
                        {formatEventDay(event.data)}
                      </span>
                      <span className={`text-[10px] font-bold tracking-wider ${index === 0 ? 'text-white/80' : ''}`} style={{ color: index === 0 ? 'white' : `${primaryColor}99` }}>
                        {formatEventMonth(event.data)}
                      </span>
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{event.nome}</h3>
                      
                      {/* Data completa e Horário */}
                      <p className="text-sm text-gray-600 mt-0.5 capitalize">
                        {formatFullDate(event.data)}
                        {event.horario && ` às ${event.horario}`}
                      </p>
                      
                      {/* Local */}
                      {event.local && (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{event.local}</span>
                        </div>
                      )}
                      
                      {/* Descrição/Observações */}
                      {event.observacoes && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                          {event.observacoes}
                        </p>
                      )}
                      
                      {/* Valor (se evento pago) */}
                      {event.valor > 0 && (
                        <p className="text-sm font-semibold text-green-600 mt-1">
                          R$ {event.valor.toFixed(2).replace('.', ',')}
                        </p>
                      )}
                    </div>
                    
                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 self-center" />
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Agenda/Schedule - Multiple Blocks Support */}
        {newsConfig?.mostrar_agenda && (agendaItems.length > 0 || programacaoBlocos.length > 0) && (
          <ProgramacaoSection 
            blocos={programacaoBlocos}
            items={agendaItems}
            primaryColor={primaryColor}
            defaultTitle={newsConfig.agenda_titulo || "Programação"}
          />
        )}

        {/* Calendário de Programação (público) */}
        <section className="px-4 py-4 border-t border-gray-100" data-testid="news-calendar-section">
          <ProgramacaoCalendar publicMode primaryColor={primaryColor} />
        </section>

        {/* Blocos de Inscrição - Seção Pública */}
        {inscricaoBlocos.length > 0 && (
          <section className="border-t border-gray-100 py-6">
            {inscricaoBlocos.map((bloco) => {
              const getBlocoIcon = (iconName) => {
                const icons = {
                  clipboard: ClipboardList,
                  calendar: Calendar,
                  heart: Heart,
                  "hand-heart": HandHeart,
                  music: Music,
                  brain: Brain,
                  graduation: GraduationCap,
                  dumbbell: Dumbbell,
                };
                return icons[iconName] || ClipboardList;
              };
              const BlocoIcon = getBlocoIcon(bloco.icone);
              
              return (
                <div key={bloco.id} className="mb-6">
                  <div className="px-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl ${bloco.cor} text-white flex items-center justify-center`}>
                        <BlocoIcon className="h-5 w-5" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                          {bloco.nome}
                        </h2>
                        {bloco.descricao && (
                          <p className="text-sm text-muted-foreground">{bloco.descricao}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Projetos do bloco */}
                  {bloco.formato_news === "destaque" ? (
                    // Formato Destaque - Banner grande
                    <div className="px-4 space-y-4">
                      {bloco.projetos?.map((projeto) => (
                        <div 
                          key={projeto.id}
                          className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-700 p-6 cursor-pointer hover:shadow-lg transition-shadow"
                          onClick={() => {
                            if (user) {
                              navigate(`/social/inscricao/${projeto.id}`);
                            } else {
                              navigate(`/inscricao-publica/${projeto.id}`);
                            }
                          }}
                        >
                          <div className="relative z-10">
                            <h3 className="text-xl font-bold text-white mb-2">{projeto.nome}</h3>
                            {projeto.objetivo && (
                              <p className="text-gray-300 text-sm mb-4 line-clamp-2">{projeto.objetivo}</p>
                            )}
                            <div className="flex flex-wrap gap-3 text-white/80 text-sm mb-4">
                              {projeto.data_evento && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  {formatDateBR(projeto.data_evento)}
                                </span>
                              )}
                              {projeto.horario_evento && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  {projeto.horario_evento}
                                </span>
                              )}
                              {projeto.valor_inscricao > 0 && (
                                <span className="font-semibold text-green-400">
                                  R$ {projeto.valor_inscricao.toFixed(2)}
                                </span>
                              )}
                            </div>
                            <Button className="bg-white text-gray-900 hover:bg-gray-100">
                              {user ? "Fazer Inscrição" : "Inscrever-se"}
                              <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Formato Cards / Lista - card moderno unificado
                    <div className={`px-4 grid grid-cols-1 gap-3 ${bloco.formato_news === "lista" ? "" : "sm:grid-cols-2"}`}>
                      {bloco.projetos?.map((projeto) => {
                        const goEnroll = () => navigate(user ? `/social/inscricao/${projeto.id}` : `/inscricao-publica/${projeto.id}`);
                        const local = projeto.local_evento || projeto.local;
                        const descricao = projeto.objetivo || projeto.descricao;
                        const dataEvento = projeto.data_evento || projeto.data_inicio;
                        return (
                          <div
                            key={projeto.id}
                            className="group relative p-4 rounded-2xl border bg-card hover:shadow-md hover:border-primary/30 cursor-pointer transition-all"
                            onClick={goEnroll}
                            data-testid={`news-project-card-${projeto.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold leading-tight">{projeto.nome}</h3>
                                {descricao && (
                                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{descricao}</p>
                                )}
                                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground mt-2">
                                  {dataEvento && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-3.5 w-3.5" />
                                      {formatDateBR(dataEvento)}{projeto.horario_evento ? ` às ${projeto.horario_evento}` : ""}
                                    </span>
                                  )}
                                  {!dataEvento && projeto.dias_horarios && (
                                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{projeto.dias_horarios}</span>
                                  )}
                                  {local && (
                                    <span className="flex items-center gap-1 min-w-0"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{local}</span></span>
                                  )}
                                  {projeto.vagas_disponiveis !== null && projeto.vagas_disponiveis !== undefined && (
                                    <span className={`flex items-center gap-1 font-medium ${projeto.vagas_disponiveis <= 0 ? "text-red-600" : "text-emerald-700"}`}>
                                      <Users className="h-3.5 w-3.5" />
                                      {projeto.vagas_disponiveis <= 0 ? "Esgotado" : `${projeto.vagas_disponiveis} vaga${projeto.vagas_disponiveis === 1 ? "" : "s"}`}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-dashed">
                              {projeto.valor_inscricao > 0 ? (
                                <span className="font-semibold text-green-600">R$ {projeto.valor_inscricao.toFixed(2)}</span>
                              ) : (
                                <span className="text-sm text-muted-foreground">Gratuito</span>
                              )}
                              <Button size="sm" className="rounded-full px-4" style={{ backgroundColor: primaryColor }} disabled={projeto.vagas_disponiveis !== null && projeto.vagas_disponiveis !== undefined && projeto.vagas_disponiveis <= 0} data-testid={`news-enroll-btn-${projeto.id}`}>
                                Inscrever
                                <ArrowRight className="h-3.5 w-3.5 ml-1" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {/* News Section - Instagram Feed Style */}
        {showNews && displayNews.length > 0 && (
          <section className="border-t border-gray-100">
            <div className="p-4">
              <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100 mb-4">Últimas Notícias</h2>
            </div>
            <div className="space-y-6">
              {displayNews.map((item) => (
                <article 
                  key={item.id}
                  className="bg-white"
                  data-testid={`news-card-${item.id}`}
                >
                  {/* Post Header */}
                  <div className="flex items-center gap-3 px-4 py-3">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {(systemSettings?.nome_igreja || "8").charAt(0)}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900">{systemSettings?.nome_igreja || "8 Life"}</p>
                      {item.data_publicacao && (
                        <p className="text-xs text-gray-500">
                          {format(new Date(item.data_publicacao), "dd 'de' MMM", { locale: ptBR })}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Post Image */}
                  {item.imagem_url && (
                    <div className="aspect-square md:aspect-video relative">
                      <img 
                        src={item.imagem_url} 
                        alt={item.titulo}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Post Content */}
                  <div className="px-4 py-3">
                    {item.categoria && (
                      <Badge 
                        variant="secondary" 
                        className="mb-2 text-xs font-medium"
                        style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                      >
                        {item.categoria}
                      </Badge>
                    )}
                    <h3 className="font-semibold text-gray-900 mb-1">{item.titulo}</h3>
                    {item.resumo && (
                      <p className="text-sm text-gray-600 line-clamp-2">{item.resumo}</p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="border-t border-gray-100 p-6 text-center">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} {systemSettings?.nome_igreja || "8 Life"}
          </p>
          <p className="text-xs text-gray-300 mt-1">
            Todos os direitos reservados
          </p>
        </footer>
      </main>

      {/* PWA Install Modal */}
      <Dialog open={showInstallModal} onOpenChange={setShowInstallModal}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Download className="h-5 w-5" style={{ color: primaryColor }} />
              Instalar Aplicativo
            </DialogTitle>
            <DialogDescription>
              Tenha acesso rápido na sua tela inicial
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {deviceInfo.isIOS ? (
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                    📱 iPhone/iPad (Safari)
                  </p>
                </div>
                <ol className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold">1</span>
                    <span>Toque no botão <strong>Compartilhar</strong> (quadrado com seta ↑) na barra inferior</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold">2</span>
                    <span>Role e toque em <strong>"Adicionar à Tela de Início"</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold">3</span>
                    <span>Toque em <strong>"Adicionar"</strong></span>
                  </li>
                </ol>
              </div>
            ) : deviceInfo.isAndroid ? (
              <div className="space-y-4">
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                    📱 Android ({deviceInfo.isSamsung ? "Samsung Browser" : deviceInfo.isChrome ? "Chrome" : "Navegador"})
                  </p>
                </div>
                
                {/* Visual instruction with arrow */}
                <div className="relative bg-gray-800 rounded-lg p-3 text-white">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-400">fecanedo.com.br</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xl animate-pulse">⋮</span>
                      <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded animate-bounce">← Toque aqui</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">Barra de endereço do navegador</p>
                </div>

                <ol className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">1</span>
                    <span>Toque no menu <strong className="bg-gray-200 px-1.5 py-0.5 rounded">⋮</strong> (três pontos) no canto superior direito do Chrome</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">2</span>
                    <span>
                      {deviceInfo.isSamsung 
                        ? <>Procure e toque em <strong>"Adicionar página à"</strong> → <strong>"Tela inicial"</strong></>
                        : <>Procure e toque em <strong>"Instalar aplicativo"</strong> ou <strong>"Adicionar à tela inicial"</strong></>
                      }
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">3</span>
                    <span>Confirme tocando em <strong>"Instalar"</strong></span>
                  </li>
                </ol>
                
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-800">
                    💡 <strong>Dica:</strong> Se não aparecer "Instalar aplicativo", procure por "Adicionar à tela inicial" no menu.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm font-medium text-purple-800 flex items-center gap-2">
                    💻 Desktop ({deviceInfo.isChrome ? "Chrome" : deviceInfo.isEdge ? "Edge" : deviceInfo.isFirefox ? "Firefox" : "Navegador"})
                  </p>
                </div>
                <ol className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold">1</span>
                    <span>Clique no ícone <strong>⊕</strong> ou <strong>📥</strong> na barra de endereço</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold">2</span>
                    <span>Clique em <strong>"Instalar"</strong></span>
                  </li>
                </ol>
              </div>
            )}
            
            <div className="pt-2 border-t">
              <Button 
                onClick={() => setShowInstallModal(false)} 
                className="w-full"
                style={{ backgroundColor: primaryColor }}
              >
                Entendi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* WiFi Portal Modal */}
      <Dialog open={showWifiModal} onOpenChange={setShowWifiModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader className="text-center pb-2">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Wifi className="h-8 w-8" style={{ color: primaryColor }} />
            </div>
            <DialogTitle className="text-xl">
              Conecte-se ao WiFi
            </DialogTitle>
            <DialogDescription>
              Preencha seus dados para navegar gratuitamente
            </DialogDescription>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex border-b mb-4">
            <button
              onClick={() => setWifiTab("register")}
              className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                wifiTab === "register" 
                  ? "border-current" 
                  : "border-transparent text-gray-500"
              }`}
              style={{ color: wifiTab === "register" ? primaryColor : undefined }}
            >
              Novo Cadastro
            </button>
            <button
              onClick={() => setWifiTab("login")}
              className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                wifiTab === "login" 
                  ? "border-current" 
                  : "border-transparent text-gray-500"
              }`}
              style={{ color: wifiTab === "login" ? primaryColor : undefined }}
            >
              Já tenho cadastro
            </button>
          </div>

          {wifiTab === "login" ? (
            <form onSubmit={handleWifiLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wifi-login-telefone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Telefone
                </Label>
                <Input
                  id="wifi-login-telefone"
                  type="tel"
                  placeholder="(62) 99999-9999"
                  value={wifiLoginForm.telefone}
                  onChange={(e) => setWifiLoginForm({ ...wifiLoginForm, telefone: e.target.value })}
                  data-testid="wifi-login-phone"
                />
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">ou</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="wifi-login-email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="wifi-login-email"
                  type="email"
                  placeholder="seu@email.com"
                  value={wifiLoginForm.email}
                  onChange={(e) => setWifiLoginForm({ ...wifiLoginForm, email: e.target.value })}
                  data-testid="wifi-login-email"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full rounded-xl" 
                disabled={wifiSubmitting}
                style={{ backgroundColor: primaryColor }}
                data-testid="wifi-login-submit"
              >
                {wifiSubmitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Wifi className="h-4 w-4 mr-2" />
                )}
                Conectar ao WiFi
              </Button>
            </form>
          ) : (
            <form onSubmit={handleWifiRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wifi-nome" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Nome Completo <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="wifi-nome"
                  placeholder="Seu nome completo"
                  value={wifiRegisterForm.nome_completo}
                  onChange={(e) => setWifiRegisterForm({ ...wifiRegisterForm, nome_completo: e.target.value })}
                  required
                  data-testid="wifi-register-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="wifi-telefone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Telefone <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="wifi-telefone"
                  type="tel"
                  placeholder="(62) 99999-9999"
                  value={wifiRegisterForm.telefone}
                  onChange={(e) => setWifiRegisterForm({ ...wifiRegisterForm, telefone: e.target.value })}
                  required
                  data-testid="wifi-register-phone"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="wifi-email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="wifi-email"
                  type="email"
                  placeholder="seu@email.com"
                  value={wifiRegisterForm.email}
                  onChange={(e) => setWifiRegisterForm({ ...wifiRegisterForm, email: e.target.value })}
                  data-testid="wifi-register-email"
                />
              </div>

              {/* Password fields - shown when email is provided */}
              {wifiRegisterForm.email && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="wifi-senha" className="flex items-center gap-2">
                      🔐 Senha <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="wifi-senha"
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      value={wifiRegisterForm.senha}
                      onChange={(e) => setWifiRegisterForm({ ...wifiRegisterForm, senha: e.target.value })}
                      data-testid="wifi-register-password"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wifi-confirmar-senha" className="flex items-center gap-2">
                      🔐 Confirmar Senha <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="wifi-confirmar-senha"
                      type="password"
                      placeholder="Repita a senha"
                      value={wifiRegisterForm.confirmar_senha}
                      onChange={(e) => setWifiRegisterForm({ ...wifiRegisterForm, confirmar_senha: e.target.value })}
                      data-testid="wifi-register-password-confirm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Esta senha será usada para acessar o sistema
                    </p>
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="wifi-sexo">Sexo</Label>
                  <Select
                    value={wifiRegisterForm.sexo}
                    onValueChange={(value) => setWifiRegisterForm({ ...wifiRegisterForm, sexo: value })}
                  >
                    <SelectTrigger id="wifi-sexo">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Masculino">Masculino</SelectItem>
                      <SelectItem value="Feminino">Feminino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="wifi-cidade">Cidade</Label>
                  <Input
                    id="wifi-cidade"
                    placeholder="Sua cidade"
                    value={wifiRegisterForm.cidade}
                    onChange={(e) => setWifiRegisterForm({ ...wifiRegisterForm, cidade: e.target.value })}
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full rounded-xl" 
                disabled={wifiSubmitting}
                style={{ backgroundColor: primaryColor }}
                data-testid="wifi-register-submit"
              >
                {wifiSubmitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Wifi className="h-4 w-4 mr-2" />
                )}
                Cadastrar e Conectar
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Banner Carousel Component (Image + Video)
function BannerCarousel({ banner, carrosselIndex, setCarrosselIndex }) {
  const [imageError, setImageError] = useState(false);
  const hasVideo = banner.video_ativo && (banner.video_url || banner.video_arquivo_url);
  const hasImage = banner.imagem_url && !imageError;
  const showCarousel = hasImage && hasVideo;
  
  // Default fallback image
  const fallbackImage = "https://images.unsplash.com/photo-1577733563933-0d02086b1a82?w=1200";
  
  // Auto-rotate carousel
  useEffect(() => {
    if (!showCarousel) return;
    
    const interval = setInterval(() => {
      setCarrosselIndex(prev => prev === 0 ? 1 : 0);
    }, (banner.carrossel_intervalo || 5) * 1000);
    
    return () => clearInterval(interval);
  }, [showCarousel, banner.carrossel_intervalo, setCarrosselIndex]);

  // Convert YouTube/Vimeo URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (ytMatch) {
      return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${ytMatch[1]}`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1`;
    }
    
    return url;
  };

  const renderVideo = () => {
    if (banner.video_tipo === "upload" && banner.video_arquivo_url) {
      return (
        <video 
          src={banner.video_arquivo_url} 
          className="w-full h-full object-cover"
          autoPlay 
          muted 
          loop 
          playsInline
        />
      );
    } else if (banner.video_url) {
      const embedUrl = getEmbedUrl(banner.video_url);
      return (
        <iframe
          src={embedUrl}
          className="w-full h-full"
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
        />
      );
    }
    return null;
  };

  return (
    <section className="aspect-[3/1] relative overflow-hidden">
      {/* Carousel Container */}
      <div className="relative w-full h-full">
        {/* Image Slide */}
        {(banner.imagem_url || !hasVideo) && (
          <div 
            className={`absolute inset-0 transition-opacity duration-700 ${
              showCarousel && carrosselIndex === 1 ? 'opacity-0' : 'opacity-100'
            }`}
          >
            <img 
              src={imageError ? fallbackImage : (banner.imagem_url || fallbackImage)} 
              alt={banner.titulo || "Banner"} 
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          </div>
        )}
        
        {/* Video Slide */}
        {hasVideo && (
          <div 
            className={`absolute inset-0 transition-opacity duration-700 ${
              showCarousel && carrosselIndex === 0 ? 'opacity-0' : 'opacity-100'
            } ${!hasImage ? 'opacity-100' : ''}`}
          >
            {renderVideo()}
          </div>
        )}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
      
      {/* Title */}
      <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 pointer-events-none">
        {banner.titulo && (
          <h2 className="text-white text-base md:text-xl font-bold line-clamp-1">
            {banner.titulo}
          </h2>
        )}
      </div>

      {/* Carousel Indicators */}
      {showCarousel && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          <button 
            onClick={() => setCarrosselIndex(0)}
            className={`w-2 h-2 rounded-full transition-all ${carrosselIndex === 0 ? 'bg-white w-4' : 'bg-white/50'}`}
          />
          <button 
            onClick={() => setCarrosselIndex(1)}
            className={`w-2 h-2 rounded-full transition-all ${carrosselIndex === 1 ? 'bg-white w-4' : 'bg-white/50'}`}
          />
        </div>
      )}
    </section>
  );
}

// Programação Section Component (Multiple Blocks with Formats)
function ProgramacaoSection({ blocos, items, primaryColor, defaultTitle }) {
  // Group items by bloco_id
  const getItemsForBloco = (blocoId) => {
    return items.filter(i => i.bloco_id === blocoId && (i.tipo === "programacao" || !i.tipo));
  };
  
  // Get unassigned items (legacy)
  const unassignedItems = items.filter(i => !i.bloco_id && (i.tipo === "programacao" || !i.tipo));
  
  // Render items based on format
  const renderByFormat = (blockItems, title, formato = "grid") => {
    if (blockItems.length === 0) return null;
    
    switch (formato) {
      case "lista":
        return (
          <div className="mb-6" key={title}>
            <div className="flex items-center justify-between mb-3 px-4">
              <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">{title}</h2>
            </div>
            <div className="px-4 space-y-2">
              {blockItems.map((item) => (
                <AgendaListItem key={item.id} item={item} primaryColor={primaryColor} />
              ))}
            </div>
          </div>
        );
      
      case "carrossel":
        return (
          <div className="mb-6" key={title}>
            <div className="flex items-center justify-between mb-3 px-4">
              <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">{title}</h2>
            </div>
            <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {blockItems.map((item) => (
                <AgendaCarouselItem key={item.id} item={item} primaryColor={primaryColor} />
              ))}
            </div>
          </div>
        );
      
      case "destaque":
        return (
          <div className="mb-6" key={title}>
            <div className="flex items-center justify-between mb-3 px-4">
              <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">{title}</h2>
            </div>
            <div className="px-4 space-y-4">
              {blockItems.slice(0, 3).map((item, idx) => (
                <AgendaFeaturedItem key={item.id} item={item} primaryColor={primaryColor} isFirst={idx === 0} />
              ))}
            </div>
          </div>
        );
      
      default: // grid
        return (
          <div className="mb-6" key={title}>
            <div className="flex items-center justify-between mb-3 px-4">
              <h2 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100">{title}</h2>
            </div>
            <div
              className="grid gap-2 px-4"
              style={{ gridTemplateColumns: `repeat(${Math.min(Math.max(blockItems.length, 1), 3)}, minmax(0, 1fr))` }}
              data-testid={`agenda-grid-${title}`}
            >
              {blockItems.slice(0, 9).map((item) => (
                <AgendaGridItem key={item.id} item={item} primaryColor={primaryColor} />
              ))}
            </div>
          </div>
        );
    }
  };

  return (
    <section className="py-4">
      {/* Render unassigned items first (legacy support) */}
      {unassignedItems.length > 0 && blocos.length === 0 && (
        renderByFormat(unassignedItems, defaultTitle, "grid")
      )}
      
      {/* Render each block with its format */}
      {blocos.map((bloco) => {
        const blocoItems = getItemsForBloco(bloco.id);
        if (blocoItems.length === 0) return null;
        return renderByFormat(blocoItems, bloco.titulo, bloco.formato || "grid");
      })}
      
      {/* If there are blocks but also unassigned items, show them as "Outros" */}
      {unassignedItems.length > 0 && blocos.length > 0 && (
        renderByFormat(unassignedItems, "Outros", "grid")
      )}
    </section>
  );
}

// Grid Item (Instagram style - square)
function AgendaGridItem({ item, primaryColor }) {
  // Format date
  const formatItemDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return format(date, "dd/MM", { locale: ptBR });
    } catch {
      return null;
    }
  };

  const content = (
    <>
      {item.imagem_url ? (
        <img
          src={item.imagem_url}
          alt={item.titulo}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      ) : (
        <div 
          className="w-full h-full flex items-center justify-center"
          style={{ backgroundColor: `${primaryColor}10` }}
        >
          <Calendar className="h-8 w-8" style={{ color: primaryColor }} />
        </div>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
        <p className="text-white text-xs font-medium leading-tight line-clamp-2">{item.titulo}</p>
        <div className="flex items-center gap-1 text-white/70 text-[10px] mt-0.5">
          {item.data && <span>{formatItemDate(item.data)}</span>}
          {item.dia_semana && !item.data && <span>{item.dia_semana}</span>}
          {item.horario && <span>• {item.horario}</span>}
        </div>
      </div>
    </>
  );

  const Wrapper = item.link_url ? 'a' : 'div';
  const wrapperProps = item.link_url ? {
    href: item.link_url,
    target: "_blank",
    rel: "noopener noreferrer"
  } : {};

  return (
    <Wrapper 
      {...wrapperProps}
      className="aspect-square relative group cursor-pointer overflow-hidden block"
      data-testid={`agenda-item-${item.id}`}
    >
      {content}
    </Wrapper>
  );
}

// List Item (vertical list with details)
function AgendaListItem({ item, primaryColor }) {
  const Wrapper = item.link_url ? 'a' : 'div';
  const wrapperProps = item.link_url ? {
    href: item.link_url,
    target: "_blank",
    rel: "noopener noreferrer"
  } : {};

  // Format date
  const formatItemDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return format(date, "dd 'de' MMM", { locale: ptBR });
    } catch {
      return null;
    }
  };

  return (
    <Wrapper 
      {...wrapperProps}
      className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
      data-testid={`agenda-list-${item.id}`}
    >
      {item.imagem_url ? (
        <img
          src={item.imagem_url}
          alt={item.titulo}
          className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
        />
      ) : (
        <div 
          className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${primaryColor}15` }}
        >
          <Calendar className="h-6 w-6" style={{ color: primaryColor }} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900 line-clamp-2">{item.titulo}</p>
        {item.descricao && (
          <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">{item.descricao}</p>
        )}
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
          {item.data && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatItemDate(item.data)}</span>}
          {item.dia_semana && <span>{item.dia_semana}</span>}
          {item.horario && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{item.horario}</span>}
        </div>
      </div>
      <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-2" />
    </Wrapper>
  );
}

// Carousel Item (horizontal scroll cards)
function AgendaCarouselItem({ item, primaryColor }) {
  const Wrapper = item.link_url ? 'a' : 'div';
  const wrapperProps = item.link_url ? {
    href: item.link_url,
    target: "_blank",
    rel: "noopener noreferrer"
  } : {};

  // Format date
  const formatItemDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return format(date, "dd/MM", { locale: ptBR });
    } catch {
      return null;
    }
  };

  return (
    <Wrapper 
      {...wrapperProps}
      className="flex-shrink-0 w-44 group cursor-pointer"
      data-testid={`agenda-carousel-${item.id}`}
    >
      <div className="relative aspect-[4/5] rounded-xl overflow-hidden mb-2 shadow-md">
        {item.imagem_url ? (
          <img
            src={item.imagem_url}
            alt={item.titulo}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div 
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <Calendar className="h-10 w-10" style={{ color: primaryColor }} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <div className="flex items-center gap-2 text-white/90 text-xs">
            {item.data && <span>{formatItemDate(item.data)}</span>}
            {item.horario && <span>• {item.horario}</span>}
          </div>
        </div>
      </div>
      <p className="font-semibold text-gray-900 text-sm line-clamp-2">{item.titulo}</p>
      {item.descricao && (
        <p className="text-gray-500 text-xs line-clamp-2 mt-0.5">{item.descricao}</p>
      )}
      {item.dia_semana && !item.data && (
        <p className="text-gray-500 text-xs">{item.dia_semana}</p>
      )}
    </Wrapper>
  );
}

// Featured Item (large cards with full details)
function AgendaFeaturedItem({ item, primaryColor, isFirst }) {
  const Wrapper = item.link_url ? 'a' : 'div';
  const wrapperProps = item.link_url ? {
    href: item.link_url,
    target: "_blank",
    rel: "noopener noreferrer"
  } : {};

  // Format date
  const formatItemDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return format(date, "dd 'de' MMM", { locale: ptBR });
    } catch {
      return null;
    }
  };

  if (isFirst) {
    // First item is large
    return (
      <Wrapper 
        {...wrapperProps}
        className="relative rounded-2xl overflow-hidden group cursor-pointer"
        data-testid={`agenda-featured-${item.id}`}
      >
        <div className="aspect-video">
          {item.imagem_url ? (
            <img
              src={item.imagem_url}
              alt={item.titulo}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div 
              className="w-full h-full flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}15` }}
            >
              <Calendar className="h-16 w-16" style={{ color: primaryColor }} />
            </div>
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-white text-lg font-bold mb-1">{item.titulo}</h3>
          {item.descricao && (
            <p className="text-white/80 text-sm mb-2 line-clamp-2">{item.descricao}</p>
          )}
          <div className="flex items-center gap-3 text-white/80 text-sm">
            {item.data && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatItemDate(item.data)}
              </span>
            )}
            {item.dia_semana && !item.data && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {item.dia_semana}
              </span>
            )}
            {item.horario && (
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {item.horario}
              </span>
            )}
          </div>
        </div>
      </Wrapper>
    );
  }

  // Other items are smaller horizontal cards
  return (
    <Wrapper 
      {...wrapperProps}
      className="flex items-start gap-4 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
      data-testid={`agenda-featured-${item.id}`}
    >
      {item.imagem_url ? (
        <img
          src={item.imagem_url}
          alt={item.titulo}
          className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
        />
      ) : (
        <div 
          className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${primaryColor}15` }}
        >
          <Calendar className="h-8 w-8" style={{ color: primaryColor }} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-gray-900 mb-1">{item.titulo}</p>
        {item.descricao && (
          <p className="text-gray-600 text-sm line-clamp-2 mb-1">{item.descricao}</p>
        )}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {item.data && <span>{formatItemDate(item.data)}</span>}
          {item.dia_semana && !item.data && <span>{item.dia_semana}</span>}
          {item.horario && <span>• {item.horario}</span>}
        </div>
      </div>
    </Wrapper>
  );
}
